import React, { useState, useEffect } from 'react';
import { Plus, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { 
  locationService, 
  buildingService, 
  floorService, 
  departmentService, 
  roomService, 
  bedService,
  clinicalStructureService
} from '../../../services/firebaseService';
import { useNotifications } from '../../NotificationSystem';
import StructureItem from './StructureItem';
import './SetupWizardSteps.css';

/** Minimal create payloads — aligned with ClinicalMapper required fields only. */
const buildSetupCreatePayload = (type, formData, organizationId) => {
  switch (type) {
    case 'location':
      return {
        name: (formData.name || '').trim(),
        address: (formData.address || '').trim(),
        organizationId,
        status: 'active'
      };
    case 'building':
      return {
        name: (formData.name || '').trim(),
        locationId: formData.locationId,
        status: 'active'
      };
    case 'floor':
      return {
        name: (formData.name || '').trim(),
        buildingId: formData.buildingId,
        status: 'active'
      };
    case 'department':
      return {
        name: (formData.name || '').trim(),
        floorId: formData.floorId,
        status: 'active'
      };
    case 'room':
      return {
        name: (formData.name || '').trim(),
        departmentId: formData.departmentId,
        status: 'active'
      };
    case 'bed':
      return {
        bedNumber: (formData.bedNumber || '').trim(),
        roomId: formData.roomId,
        status: 'active'
      };
    default:
      return { status: 'active' };
  }
};

const buildSetupUpdatePayload = (type, updates) => {
  switch (type) {
    case 'bed':
      return { bedNumber: (updates.bedNumber || updates.number || '').trim() };
    default:
      return { name: (updates.name || updates.number || '').trim() };
  }
};

const Step2ClinicalStructure = ({ stepData, onComplete, onValidation, structure, setStructure }) => {
  const { notify } = useNotifications();
  const { t } = useTranslation('setup');
  const [clinicalData, setClinicalData] = useState({
    locations: [],
    buildings: [],
    floors: [],
    departments: [],
    rooms: [],
    beds: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [organizationId, setOrganizationId] = useState(null);
  
  // Forms state
  const [showForm, setShowForm] = useState(null); // 'location', 'building', 'floor', 'department', 'room', 'bed'
  const [formData, setFormData] = useState({});

  // Get organization ID from structure
  useEffect(() => {
    if (structure?.organizations && structure.organizations.length > 0) {
      setOrganizationId(structure.organizations[0].id);
    }
  }, [structure]);

  // Load existing clinical structure for current organization
  useEffect(() => {
    const loadStructure = async () => {
      if (!organizationId) {
        // No organization yet, clear data
        setClinicalData({
          locations: [],
          buildings: [],
          floors: [],
          departments: [],
          rooms: [],
          beds: []
        });
        return;
      }

      try {
        // Get locations for this organization only
        const allLocations = await clinicalStructureService.getLocationsByOrganization(organizationId);
        
        // Strictly filter and deduplicate locations by ID
        // Only include locations that match organizationId exactly
        const locationsMap = new Map();
        (allLocations || []).forEach(loc => {
          // Double-check organizationId matches (defense in depth)
          // Also ensure we have a valid ID
          if (loc && loc.id && loc.organizationId && String(loc.organizationId) === String(organizationId)) {
            // If we already have this location ID, keep the first one (deduplication)
            if (!locationsMap.has(loc.id)) {
              locationsMap.set(loc.id, loc);
            }
          }
        });
        const locations = Array.from(locationsMap.values());
        
        // Additional safety: if we got more locations than expected, log it
        if (allLocations && allLocations.length > locations.length) {
          console.warn('Some locations were filtered out:', {
            total: allLocations.length,
            filtered: locations.length,
            organizationId,
            reasons: allLocations.filter(loc => 
              !loc.id || !loc.organizationId || String(loc.organizationId) !== String(organizationId)
            ).map(loc => ({
              id: loc.id,
              hasOrgId: !!loc.organizationId,
              orgId: loc.organizationId
            }))
          });
        }
        
        // Get all buildings for these locations
        const buildingPromises = locations.map(loc => 
          clinicalStructureService.getBuildingsByLocation(loc.id)
        );
        const buildingsArrays = await Promise.all(buildingPromises);
        const buildingsAll = buildingsArrays.flat();
        
        // Deduplicate buildings by ID
        const buildingsMap = new Map();
        buildingsAll.forEach(building => {
          if (building.id) {
            buildingsMap.set(building.id, building);
          }
        });
        const buildings = Array.from(buildingsMap.values());

        // Get all floors for these buildings
        const floorPromises = buildings.map(building => 
          clinicalStructureService.getFloorsByBuilding(building.id)
        );
        const floorsArrays = await Promise.all(floorPromises);
        const floorsAll = floorsArrays.flat();
        
        // Deduplicate floors by ID
        const floorsMap = new Map();
        floorsAll.forEach(floor => {
          if (floor.id) {
            floorsMap.set(floor.id, floor);
          }
        });
        const floors = Array.from(floorsMap.values());

        // Get all departments for these floors
        const departmentPromises = floors.map(floor => 
          clinicalStructureService.getDepartmentsByFloor(floor.id)
        );
        const departmentsArrays = await Promise.all(departmentPromises);
        const departmentsAll = departmentsArrays.flat();
        
        // Deduplicate departments by ID
        const departmentsMap = new Map();
        departmentsAll.forEach(dept => {
          if (dept.id) {
            departmentsMap.set(dept.id, dept);
          }
        });
        const departments = Array.from(departmentsMap.values());

        // Get all rooms for these departments
        const roomPromises = departments.map(dept => 
          clinicalStructureService.getRoomsByDepartment(dept.id)
        );
        const roomsArrays = await Promise.all(roomPromises);
        const roomsAll = roomsArrays.flat();
        
        // Deduplicate rooms by ID
        const roomsMap = new Map();
        roomsAll.forEach(room => {
          if (room.id) {
            roomsMap.set(room.id, room);
          }
        });
        const rooms = Array.from(roomsMap.values());

        // Get all beds for these rooms
        const bedPromises = rooms.map(room => 
          clinicalStructureService.getBedsByRoom(room.id)
        );
        const bedsArrays = await Promise.all(bedPromises);
        const bedsAll = bedsArrays.flat();
        
        // Deduplicate beds by ID
        const bedsMap = new Map();
        bedsAll.forEach(bed => {
          if (bed.id) {
            bedsMap.set(bed.id, bed);
          }
        });
        const beds = Array.from(bedsMap.values());

        setClinicalData({
          locations: locations,
          buildings: buildings,
          floors: floors,
          departments: departments,
          rooms: rooms,
          beds: beds
        });

        // Check if minimum requirements are met
        checkMinimumRequirements(locations, buildings, floors, departments, rooms, beds);
      } catch (error) {
        console.error('Error loading clinical structure:', error);
        notify.error(t('step2.notifications.loadFailed'), t('step2.notifications.loadFailedTitle'));
      }
    };
    loadStructure();
  }, [organizationId]);

  const checkMinimumRequirements = (locations, buildings, floors, departments, rooms, beds) => {
    const requirements = {
      locations: locations.length >= 1,
      buildings: buildings.length >= 1,
      floors: floors.length >= 1,
      departments: departments.length >= 1,
      rooms: rooms.length >= 1,
      beds: beds.length >= 1
    };

    const allMet = Object.values(requirements).every(met => met);
    
    if (allMet) {
      onComplete({
        locations: locations.length,
        buildings: buildings.length,
        floors: floors.length,
        departments: departments.length,
        rooms: rooms.length,
        beds: beds.length,
        requirementsMet: true
      });
    }
  };

  const handleCreate = async (type) => {
    setLoading(true);
    setErrors({});

    try {
      let result;
      const data = buildSetupCreatePayload(type, formData, organizationId);

      switch (type) {
        case 'location':
          if (!organizationId) {
            throw new Error(t('step2.internalErrors.organizationRequired'));
          }
          data.organizationId = organizationId;
          result = await locationService.create(data);
          break;
        case 'building':
          if (!data.locationId) {
            throw new Error(t('step2.internalErrors.selectLocation'));
          }
          result = await buildingService.create(data);
          break;
        case 'floor':
          if (!data.buildingId) {
            throw new Error(t('step2.internalErrors.selectBuilding'));
          }
          result = await floorService.create(data);
          break;
        case 'department':
          if (!data.floorId) {
            throw new Error(t('step2.internalErrors.selectFloor'));
          }
          result = await departmentService.create(data);
          break;
        case 'room':
          if (!data.departmentId) {
            throw new Error(t('step2.internalErrors.selectDepartment'));
          }
          result = await roomService.create(data);
          break;
        case 'bed':
          if (!data.roomId) {
            throw new Error(t('step2.internalErrors.selectRoom'));
          }
          result = await bedService.create(data);
          break;
        default:
          throw new Error(t('step2.internalErrors.unknownType'));
      }

      // Reload structure for this organization
      const locations = await clinicalStructureService.getLocationsByOrganization(organizationId);
      
      const buildingPromises = locations.map(loc => 
        clinicalStructureService.getBuildingsByLocation(loc.id)
      );
      const buildingsArrays = await Promise.all(buildingPromises);
      const buildings = buildingsArrays.flat();

      const floorPromises = buildings.map(building => 
        clinicalStructureService.getFloorsByBuilding(building.id)
      );
      const floorsArrays = await Promise.all(floorPromises);
      const floors = floorsArrays.flat();

      const departmentPromises = floors.map(floor => 
        clinicalStructureService.getDepartmentsByFloor(floor.id)
      );
      const departmentsArrays = await Promise.all(departmentPromises);
      const departments = departmentsArrays.flat();

      const roomPromises = departments.map(dept => 
        clinicalStructureService.getRoomsByDepartment(dept.id)
      );
      const roomsArrays = await Promise.all(roomPromises);
      const rooms = roomsArrays.flat();

      const bedPromises = rooms.map(room => 
        clinicalStructureService.getBedsByRoom(room.id)
      );
      const bedsArrays = await Promise.all(bedPromises);
      const beds = bedsArrays.flat();

      setClinicalData({
        locations: locations || [],
        buildings: buildings || [],
        floors: floors || [],
        departments: departments || [],
        rooms: rooms || [],
        beds: beds || []
      });

      if (setStructure) {
        setStructure(prev => ({
          ...prev,
          locations: locations || [],
          buildings: buildings || [],
          floors: floors || [],
          departments: departments || [],
          rooms: rooms || [],
          beds: beds || []
        }));
      }

      notify.success(t(`step2.notifications.${type}.created`), t('step2.notifications.createTitle'));
      setShowForm(null);
      setFormData({});
      
      checkMinimumRequirements(locations || [], buildings || [], floors || [], departments || [], rooms || [], beds || []);
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      notify.error(t(`step2.notifications.${type}.createFailed`, { message: error.message }), t('step2.notifications.createFailedTitle'));
      setErrors({ [type]: error.message });
    } finally {
      setLoading(false);
    }
  };

  const renderForm = (type) => {
    if (showForm !== type) return null;

    const getFormFields = () => {
      switch (type) {
        case 'location':
          return (
            <>
              <div className="form-group">
                <label>{t('step2.forms.location.name')}</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('step2.forms.location.namePlaceholder')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('step2.forms.location.address')}</label>
                <input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder={t('step2.forms.location.addressPlaceholder')}
                />
              </div>
            </>
          );

        case 'building':
          return (
            <>
              <div className="form-group">
                <label>{t('step2.forms.building.selectLocation')}</label>
                <select
                  value={formData.locationId || ''}
                  onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                  required
                >
                  <option value="">{t('step2.forms.building.selectLocationPlaceholder')}</option>
                  {clinicalData.locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('step2.forms.building.name')}</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('step2.forms.building.namePlaceholder')}
                  required
                />
              </div>
            </>
          );

        case 'floor':
          return (
            <>
              <div className="form-group">
                <label>{t('step2.forms.floor.selectBuilding')}</label>
                <select
                  value={formData.buildingId || ''}
                  onChange={(e) => setFormData({ ...formData, buildingId: e.target.value })}
                  required
                >
                  <option value="">{t('step2.forms.floor.selectBuildingPlaceholder')}</option>
                  {clinicalData.buildings.map(building => (
                    <option key={building.id} value={building.id}>{building.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('step2.forms.floor.name')}</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('step2.forms.floor.namePlaceholder')}
                  required
                />
              </div>
            </>
          );

        case 'department':
          return (
            <>
              <div className="form-group">
                <label>{t('step2.forms.department.selectFloor')}</label>
                <select
                  value={formData.floorId || ''}
                  onChange={(e) => setFormData({ ...formData, floorId: e.target.value })}
                  required
                >
                  <option value="">{t('step2.forms.department.selectFloorPlaceholder')}</option>
                  {clinicalData.floors.map(floor => (
                    <option key={floor.id} value={floor.id}>{floor.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('step2.forms.department.name')}</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('step2.forms.department.namePlaceholder')}
                  required
                />
              </div>
            </>
          );

        case 'room':
          return (
            <>
              <div className="form-group">
                <label>{t('step2.forms.room.selectDepartment')}</label>
                <select
                  value={formData.departmentId || ''}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  required
                >
                  <option value="">{t('step2.forms.room.selectDepartmentPlaceholder')}</option>
                  {clinicalData.departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('step2.forms.room.name')}</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('step2.forms.room.namePlaceholder')}
                  required
                />
              </div>
            </>
          );

        case 'bed':
          return (
            <>
              <div className="form-group">
                <label>{t('step2.forms.bed.selectRoom')}</label>
                <select
                  value={formData.roomId || ''}
                  onChange={(e) => setFormData({ ...formData, roomId: e.target.value })}
                  required
                >
                  <option value="">{t('step2.forms.bed.selectRoomPlaceholder')}</option>
                  {clinicalData.rooms.map(room => (
                    <option key={room.id} value={room.id}>{room.name} {room.number ? `(${room.number})` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('step2.forms.bed.number')}</label>
                <input
                  type="text"
                  value={formData.bedNumber || ''}
                  onChange={(e) => setFormData({ ...formData, bedNumber: e.target.value })}
                  placeholder={t('step2.forms.bed.numberPlaceholder')}
                  required
                />
              </div>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <div className="create-form" style={{ 
        marginTop: '20px', 
        padding: '20px', 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px',
        backgroundColor: '#fafafa'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h4 style={{ margin: 0 }}>{t(`step2.createNewHeader.${type}`)}</h4>
          <button
            type="button"
            onClick={() => {
              setShowForm(null);
              setFormData({});
              setErrors({});
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>
        
        {getFormFields()}
        
        {errors[type] && (
          <div style={{ color: '#c33', fontSize: '14px', marginTop: '12px' }}>
            {errors[type]}
          </div>
        )}
        
        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleCreate(type)}
            disabled={loading}
          >
            {loading ? t('step2.creating') : t('step2.create')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setShowForm(null);
              setFormData({});
              setErrors({});
            }}
            disabled={loading}
          >
            {t('step2.cancel')}
          </button>
        </div>
      </div>
    );
  };

  const requirements = {
    locations: clinicalData.locations.length >= 1,
    buildings: clinicalData.buildings.length >= 1,
    floors: clinicalData.floors.length >= 1,
    departments: clinicalData.departments.length >= 1,
    rooms: clinicalData.rooms.length >= 1,
    beds: clinicalData.beds.length >= 1
  };

  const allRequirementsMet = Object.values(requirements).every(met => met);

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step2.title')}</h3>
        <p>{t('step2.description1')}</p>
        <p style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>
          {t('step2.description2')}
        </p>
      </div>

      {!organizationId && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step2.completeStep1Warning')}
        </div>
      )}

      {/* Creation Buttons */}
      <div className="creation-buttons-section" style={{ marginTop: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowForm(showForm === 'location' ? null : 'location')}
            disabled={!organizationId}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
          >
            <Plus size={18} />
            {t('step2.buttons.createLocation')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowForm(showForm === 'building' ? null : 'building')}
            disabled={!organizationId || clinicalData.locations.length === 0}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
          >
            <Plus size={18} />
            {t('step2.buttons.createBuilding')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowForm(showForm === 'floor' ? null : 'floor')}
            disabled={!organizationId || clinicalData.buildings.length === 0}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
          >
            <Plus size={18} />
            {t('step2.buttons.createFloor')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowForm(showForm === 'department' ? null : 'department')}
            disabled={!organizationId || clinicalData.floors.length === 0}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
          >
            <Plus size={18} />
            {t('step2.buttons.createDepartment')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowForm(showForm === 'room' ? null : 'room')}
            disabled={!organizationId || clinicalData.departments.length === 0}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
          >
            <Plus size={18} />
            {t('step2.buttons.createRoom')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowForm(showForm === 'bed' ? null : 'bed')}
            disabled={!organizationId || clinicalData.rooms.length === 0}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
          >
            <Plus size={18} />
            {t('step2.buttons.createBed')}
          </button>
        </div>
      </div>

      {/* Forms */}
      {renderForm('location')}
      {renderForm('building')}
      {renderForm('floor')}
      {renderForm('department')}
      {renderForm('room')}
      {renderForm('bed')}

      {/* Requirements Status */}
      <div className="requirements-section" style={{ marginTop: '32px' }}>
        <h3>{t('step2.minimumRequirements')}</h3>
        <div className="requirements-list">
          <div className={`requirement-item ${requirements.locations ? 'met' : 'pending'}`}>
            {requirements.locations ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step2.requirements.location', { count: clinicalData.locations.length })}</span>
          </div>
          <div className={`requirement-item ${requirements.buildings ? 'met' : 'pending'}`}>
            {requirements.buildings ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step2.requirements.building', { count: clinicalData.buildings.length })}</span>
          </div>
          <div className={`requirement-item ${requirements.floors ? 'met' : 'pending'}`}>
            {requirements.floors ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step2.requirements.floor', { count: clinicalData.floors.length })}</span>
          </div>
          <div className={`requirement-item ${requirements.departments ? 'met' : 'pending'}`}>
            {requirements.departments ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step2.requirements.department', { count: clinicalData.departments.length })}</span>
          </div>
          <div className={`requirement-item ${requirements.rooms ? 'met' : 'pending'}`}>
            {requirements.rooms ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step2.requirements.room', { count: clinicalData.rooms.length })}</span>
          </div>
          <div className={`requirement-item ${requirements.beds ? 'met' : 'pending'}`}>
            {requirements.beds ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step2.requirements.bed', { count: clinicalData.beds.length })}</span>
          </div>
        </div>

        {allRequirementsMet && (
          <div className="success-message" style={{ marginTop: '20px' }}>
            {t('step2.allRequirementsMet')}
          </div>
        )}
      </div>

      {/* Show existing structure for viewing/editing */}
      {(clinicalData.locations.length > 0 || clinicalData.buildings.length > 0 || clinicalData.departments.length > 0) && (
        <div className="created-structure-section" style={{ marginTop: '32px' }}>
          <h3>{t('step2.yourClinicalStructure')}</h3>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
            {t('step2.viewEditDescription')}
          </p>
          
          <div className="structure-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {clinicalData.locations
              .filter(location => location && location.id && location.organizationId === organizationId)
              .map((location, index, array) => {
                // Additional safety: deduplicate by ID in render
                const isDuplicate = array.findIndex(l => l.id === location.id) !== index;
                if (isDuplicate) return null;
                
                return (
                  <StructureItem 
                    key={location.id} 
                    item={location}
                    type="location"
                    onEdit={async (updates) => {
                      try {
                        await locationService.update(location.id, buildSetupUpdatePayload('location', updates));
                        const updated = await clinicalStructureService.getLocationsByOrganization(organizationId);
                        // Deduplicate on reload
                        const updatedMap = new Map();
                        (updated || []).forEach(loc => {
                          if (loc && loc.id && loc.organizationId === organizationId) {
                            if (!updatedMap.has(loc.id)) {
                              updatedMap.set(loc.id, loc);
                            }
                          }
                        });
                        setClinicalData(prev => ({ ...prev, locations: Array.from(updatedMap.values()) }));
                        notify.success(t('step2.notifications.location.updated'), t('step2.notifications.updateTitle'));
                      } catch (error) {
                        notify.error(t('step2.notifications.location.updateFailed', { message: error.message }), t('step2.notifications.updateFailedTitle'));
                      }
                    }}
                  />
                );
              })
              .filter(Boolean)}
            
            {clinicalData.buildings.map(building => (
              <StructureItem 
                key={building.id} 
                item={building}
                type="building"
                onEdit={async (updates) => {
                  try {
                    await buildingService.update(building.id, buildSetupUpdatePayload('building', updates));
                    const locations = await clinicalStructureService.getLocationsByOrganization(organizationId);
                    const buildingPromises = locations.map(loc => 
                      clinicalStructureService.getBuildingsByLocation(loc.id)
                    );
                    const buildingsArrays = await Promise.all(buildingPromises);
                    const updated = buildingsArrays.flat();
                    setClinicalData(prev => ({ ...prev, buildings: updated || [] }));
                    notify.success(t('step2.notifications.building.updated'), t('step2.notifications.updateTitle'));
                  } catch (error) {
                    notify.error(t('step2.notifications.building.updateFailed', { message: error.message }), t('step2.notifications.updateFailedTitle'));
                  }
                }}
              />
            ))}
            
            {clinicalData.floors.map(floor => (
              <StructureItem 
                key={floor.id} 
                item={floor}
                type="floor"
                onEdit={async (updates) => {
                  try {
                    await floorService.update(floor.id, buildSetupUpdatePayload('floor', updates));
                    const locations = await clinicalStructureService.getLocationsByOrganization(organizationId);
                    const buildingPromises = locations.map(loc => 
                      clinicalStructureService.getBuildingsByLocation(loc.id)
                    );
                    const buildingsArrays = await Promise.all(buildingPromises);
                    const buildings = buildingsArrays.flat();
                    const floorPromises = buildings.map(building => 
                      clinicalStructureService.getFloorsByBuilding(building.id)
                    );
                    const floorsArrays = await Promise.all(floorPromises);
                    const updated = floorsArrays.flat();
                    setClinicalData(prev => ({ ...prev, floors: updated || [] }));
                    notify.success(t('step2.notifications.floor.updated'), t('step2.notifications.updateTitle'));
                  } catch (error) {
                    notify.error(t('step2.notifications.floor.updateFailed', { message: error.message }), t('step2.notifications.updateFailedTitle'));
                  }
                }}
              />
            ))}
            
            {clinicalData.departments.map(department => (
              <StructureItem 
                key={department.id} 
                item={department}
                type="department"
                onEdit={async (updates) => {
                  try {
                    await departmentService.update(department.id, buildSetupUpdatePayload('department', updates));
                    const locations = await clinicalStructureService.getLocationsByOrganization(organizationId);
                    const buildingPromises = locations.map(loc => 
                      clinicalStructureService.getBuildingsByLocation(loc.id)
                    );
                    const buildingsArrays = await Promise.all(buildingPromises);
                    const buildings = buildingsArrays.flat();
                    const floorPromises = buildings.map(building => 
                      clinicalStructureService.getFloorsByBuilding(building.id)
                    );
                    const floorsArrays = await Promise.all(floorPromises);
                    const floors = floorsArrays.flat();
                    const departmentPromises = floors.map(floor => 
                      clinicalStructureService.getDepartmentsByFloor(floor.id)
                    );
                    const departmentsArrays = await Promise.all(departmentPromises);
                    const updated = departmentsArrays.flat();
                    setClinicalData(prev => ({ ...prev, departments: updated || [] }));
                    notify.success(t('step2.notifications.department.updated'), t('step2.notifications.updateTitle'));
                  } catch (error) {
                    notify.error(t('step2.notifications.department.updateFailed', { message: error.message }), t('step2.notifications.updateFailedTitle'));
                  }
                }}
              />
            ))}
            
            {clinicalData.rooms.map(room => (
              <StructureItem 
                key={room.id} 
                item={room}
                type="room"
                onEdit={async (updates) => {
                  try {
                    await roomService.update(room.id, buildSetupUpdatePayload('room', updates));
                    const locations = await clinicalStructureService.getLocationsByOrganization(organizationId);
                    const buildingPromises = locations.map(loc => 
                      clinicalStructureService.getBuildingsByLocation(loc.id)
                    );
                    const buildingsArrays = await Promise.all(buildingPromises);
                    const buildings = buildingsArrays.flat();
                    const floorPromises = buildings.map(building => 
                      clinicalStructureService.getFloorsByBuilding(building.id)
                    );
                    const floorsArrays = await Promise.all(floorPromises);
                    const floors = floorsArrays.flat();
                    const departmentPromises = floors.map(floor => 
                      clinicalStructureService.getDepartmentsByFloor(floor.id)
                    );
                    const departmentsArrays = await Promise.all(departmentPromises);
                    const departments = departmentsArrays.flat();
                    const roomPromises = departments.map(dept => 
                      clinicalStructureService.getRoomsByDepartment(dept.id)
                    );
                    const roomsArrays = await Promise.all(roomPromises);
                    const updated = roomsArrays.flat();
                    setClinicalData(prev => ({ ...prev, rooms: updated || [] }));
                    notify.success(t('step2.notifications.room.updated'), t('step2.notifications.updateTitle'));
                  } catch (error) {
                    notify.error(t('step2.notifications.room.updateFailed', { message: error.message }), t('step2.notifications.updateFailedTitle'));
                  }
                }}
              />
            ))}
            
            {clinicalData.beds.map(bed => (
              <StructureItem 
                key={bed.id} 
                item={bed}
                type="bed"
                onEdit={async (updates) => {
                  try {
                    await bedService.update(bed.id, buildSetupUpdatePayload('bed', updates));
                    const locations = await clinicalStructureService.getLocationsByOrganization(organizationId);
                    const buildingPromises = locations.map(loc => 
                      clinicalStructureService.getBuildingsByLocation(loc.id)
                    );
                    const buildingsArrays = await Promise.all(buildingPromises);
                    const buildings = buildingsArrays.flat();
                    const floorPromises = buildings.map(building => 
                      clinicalStructureService.getFloorsByBuilding(building.id)
                    );
                    const floorsArrays = await Promise.all(floorPromises);
                    const floors = floorsArrays.flat();
                    const departmentPromises = floors.map(floor => 
                      clinicalStructureService.getDepartmentsByFloor(floor.id)
                    );
                    const departmentsArrays = await Promise.all(departmentPromises);
                    const departments = departmentsArrays.flat();
                    const roomPromises = departments.map(dept => 
                      clinicalStructureService.getRoomsByDepartment(dept.id)
                    );
                    const roomsArrays = await Promise.all(roomPromises);
                    const rooms = roomsArrays.flat();
                    const bedPromises = rooms.map(room => 
                      clinicalStructureService.getBedsByRoom(room.id)
                    );
                    const bedsArrays = await Promise.all(bedPromises);
                    const updated = bedsArrays.flat();
                    setClinicalData(prev => ({ ...prev, beds: updated || [] }));
                    notify.success(t('step2.notifications.bed.updated'), t('step2.notifications.updateTitle'));
                  } catch (error) {
                    notify.error(t('step2.notifications.bed.updateFailed', { message: error.message }), t('step2.notifications.updateFailedTitle'));
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="manual-setup-section" style={{ marginTop: '32px' }}>
        <h3>{t('step2.advancedSetup')}</h3>
        <p>{t('step2.advancedSetupDescription')}</p>
        <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
          <li>{t('step2.advancedSetupLinks.locations')}</li>
          <li>{t('step2.advancedSetupLinks.buildings')}</li>
          <li>{t('step2.advancedSetupLinks.floors')}</li>
          <li>{t('step2.advancedSetupLinks.departments')}</li>
          <li>{t('step2.advancedSetupLinks.rooms')}</li>
          <li>{t('step2.advancedSetupLinks.beds')}</li>
        </ul>
      </div>
    </div>
  );
};

export default Step2ClinicalStructure;
